import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import CheckItem from '../../components/CheckItem/CheckItem';
import BulletList from '../../components/BulletList/BulletList';
import WebcamCapture from '../../components/WebcamCapture/WebcamCapture';
import useApplicationUid from '../../hooks/useApplicationUid';
import { socureUploadDocument } from '../../store/actions/documents/documentsActions';
import { PAGES } from '../../constants/pages';
import BaseLayout from '../../components/BaseLayout';
import Header from '../../components/Header';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE_GTM,
  KYC_SELECTED_OPTION,
} from '../../constants/sessionStorage';
import {
  KYC_DOCUMENT_TYPES,
  KYC_PERSON_TARGET_TYPES,
} from '../../util/constants';
import Paragraph from '../../components/Paragraph/Paragraph';
import themeColors from '../../assets/styles/themeColors';
import { separateByUppercaseAndJoinedWithHorLine } from '../../util/helpers/stringHelpers';
import { useApplicantData } from '../../hooks/useApplicantData';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';

// const DropZoneWrapperDouble = styled.div`
//   display: grid;
//   grid-template-columns: 274px 274px;
//   grid-gap: 1.25rem;

//   @media only screen and (max-width: 650px) {
//     grid-template-columns: 1fr;
//   }
// `;

const DropZoneWrapperSingle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const KycUploadSocialSecurityDocumentsViaWebcam = ({
  applicantType,
  documentType,
}) => {
  const { ApplicationUid } = useApplicationUid();
  const ApplicantData = useApplicantData();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const selectedDocument = retrieveFromSessionStorage(KYC_SELECTED_OPTION);
  const [errors, setErrors] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState({ Document: '' });

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Kyc camera',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Kyc camera',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
      customParams: {
        timestamp: new Date().toISOString(),
      },
    });
  };

  const onSubmit = () => {
    const formData = new FormData();
    formData.append('Document', uploadedDocuments.Document);
    formData.append(
      'Caption',
      KYC_DOCUMENT_TYPES[`${selectedDocument.DocumentType}`],
    );

    const onSuccess = () => {
      history.push(PAGES.KYC_VERIFICATION);
      handleGtmEvent();
    };
    const params = {
      personTargetType:
        KYC_PERSON_TARGET_TYPES[selectedDocument?.PersonTargetType],
      documentType: KYC_DOCUMENT_TYPES[`${selectedDocument?.DocumentType}`],
      socureDocumentType: selectedDocument.SocureDocumentType,
    };

    dispatch(
      socureUploadDocument({
        ApplicationUid,
        params,
        data: formData,
        onSuccess,
        setErrors,
      }),
    );
  };

  const getPreviousPath = () =>
    PAGES.KYC_PROOF_OF_SOCIAL_SECURITY[String(applicantType).toUpperCase()]
      .UPLOAD[String(separateByUppercaseAndJoinedWithHorLine(documentType))];

  return (
    <BaseLayout previousPath={getPreviousPath()} isKycProcess="true" noLogoBox>
      <Header
        titleMb={32}
        title={t('kycVerification.proofOfSocialSecurityTitleWebcam')}
      />
      <BulletList mb={24}>
        <CheckItem
          text={t('kycVerification.checkSSCItemOne')}
          color={themeColors.colorTextLight}
        />
        <CheckItem
          text={t('kycVerification.checkSSCItemTwo')}
          color={themeColors.colorTextLight}
        />
        <CheckItem
          text={t('kycVerification.checkSSCItemThree')}
          color={themeColors.colorTextLight}
        />
      </BulletList>
      <DropZoneWrapperSingle>
        <WebcamCapture
          frontSide
          onCapture={(image) => {
            setUploadedDocuments({
              ...uploadedDocuments,
              Document: image,
            });
          }}
        />
      </DropZoneWrapperSingle>
      <Paragraph color={themeColors.colorErrorMessage} variant="normal">
        {errors}
      </Paragraph>
      <OnBoardingCta
        disabled={!uploadedDocuments.Document}
        onClick={onSubmit}
      />
    </BaseLayout>
  );
};

KycUploadSocialSecurityDocumentsViaWebcam.propTypes = {
  applicantType: PropTypes.string,
  documentType: PropTypes.string,
};

export default KycUploadSocialSecurityDocumentsViaWebcam;
